import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { IconClose, IconLocate, IconPinSolid, IconPlus } from '../components/Icon';
import { useVenues } from '../lib/content';
import { useScheduleModel } from '../lib/schedule';
import { fmtDay, fmtTime } from '../lib/time';
import { usePanZoom } from '../lib/panzoom';
import ResortPlan from '../components/ResortPlan';
import { useFlags } from '../lib/content';
import { useLiveLocation, type LocateState } from '../lib/geo';
import PlanPicker, { pickAt, type Pick } from '../components/PlanPicker';

/**
 * Map — the resort plan, the venue pins, and the sheet the requirements
 * describe. There is still no tile layer: the plan is a drawing traced off the
 * resort's own site map (see ResortPlan), not a survey, so it is shown as what
 * it is rather than georeferenced onto tiles it cannot honestly line up with.
 *
 * Three behaviours are load-bearing and should survive whatever this grows into:
 * location permission is requested on tap and never on load; an indoor venue
 * says its building and floor instead of promising a GPS fix it will not get;
 * and pin labels stay hidden until the guest zooms in, because seven labels
 * across a 390 px phone is a pile of text, not a map.
 */

/**
 * Labels appear once the plan draws at least this wide on screen. Measuring the
 * drawn width rather than the zoom level is what makes the rule hold on both a
 * phone and a laptop.
 *
 * Retuned 31 Aug, from 980. That number was right for the old plan, which was
 * landscape and whose home view FILLED the viewport — it drew about a thousand
 * pixels wide, so 980 meant "at home zoom and beyond". The real plan is
 * portrait and its home view fits, so it draws nearer four hundred, and 980
 * silently meant "never": every venue name was hidden until you zoomed, except
 * whichever one was next up, which keeps its label regardless. The threshold
 * did not break, its premise did.
 *
 * 300 is the equivalent setting for a plan that draws at ~400 px, so names are
 * there when the map opens on anything down to a small phone.
 */
const LABEL_AT_PX = 300;

export default function MapPage() {
  const { venueId } = useParams();
  const navigate = useNavigate();
  const model = useScheduleModel();
  const venueDoc = useVenues();
  const wrapRef = useRef<HTMLDivElement>(null);
  const { view, panning, planWidth, wasDragged, focusOn, reset, zoomBy, handlers } = usePanZoom(wrapRef);
  const flags = useFlags();
  const { state: locating, fix, start, stop } = useLiveLocation();
  // `/map?pick` — the build tool for the two hand-owned files. See PlanPicker.
  const [search, setSearch] = useSearchParams();
  const picking = search.has('pick');
  const [pick, setPick] = useState<Pick>(() => pickAt({ x: 50, y: 50 }));
  /** Buildings collected into a wing while picking, and its label. Lifted here
   *  rather than kept in the panel so the plan can draw them with the real
   *  highlight — picking and the finished result share one code path, so there
   *  is no way for the preview to flatter the outcome. */
  const [group, setGroup] = useState<number[]>([]);
  const [groupLabel, setGroupLabel] = useState('');

  const venues = venueDoc.data?.venues ?? [];
  const confirmedWings = venueDoc.data?.resort.wings ?? {};
  // Only claim a wing that actually has buildings behind it. Without this the
  // key reads "most of us are in wings 2, 3 and 5" over a plan with nothing
  // highlighted — the caption promising something the drawing does not show.
  const stayWings = (venueDoc.data?.resort.stayWings ?? []).filter(
    (w) => (confirmedWings[w]?.length ?? 0) > 0,
  );
  // While picking, the in-progress group is drawn as if it were real.
  const planWings = picking && group.length > 0
    ? { ...confirmedWings, [groupLabel || '?']: group }
    : confirmedWings;
  const planStay = picking && group.length > 0 ? [...stayWings, groupLabel || '?'] : stayWings;
  const focused = venueId ? venues.find((v) => v.id === venueId) : undefined;
  const nextVenueId = model.next?.venueId ?? model.current?.venueId;
  const nextVenue = venues.find((v) => v.id === nextVenueId);

  // Opening /map/:venueId is a request to be shown that venue, so the plan
  // zooms to it. With no venue asked for, the map opens on wherever the next
  // event is — that is the question a guest opens this page to answer — and
  // only falls back to the middle of the resort when nothing is coming up.
  useEffect(() => {
    if (focused) {
      focusOn(focused.x, focused.y, 2.4);
      return;
    }
    const opening = venues.find((v) => v.id === nextVenueId);
    if (opening) focusOn(opening.x, opening.y);
    else reset();
    // venues is rebuilt on every content fetch; the ids are what matter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focused?.id, nextVenueId, venues.length, focusOn, reset]);

  // Live location is real now, and off by default. What made it impossible
  // before was not the browser API but the drawing: the old plan was stretched
  // along the shore and squashed inland by roughly half, so no transform could
  // put a fix in the right place. The plan is generated from coordinates now,
  // which makes lib/geo.ts's transform the exact inverse of the projection that
  // drew it. The remaining gap is naming, not geometry — see venue.json's
  // `$tbc` — which is what `liveLocation` gates.
  const liveLocation = flags.data?.liveLocation === true;

  const showLabels = planWidth >= LABEL_AT_PX;

  return (
    <div className="page page-fill">
      <div
        className={`map-wrap${panning ? ' is-panning' : ''}`}
        ref={wrapRef}
        {...handlers}
      >
        <div
          className="map-stage"
          onClickCapture={(e) => {
            if (!picking) return;
            // Capture phase and stopPropagation, so tapping a venue pin reports
            // the pin's position instead of navigating away from the picker.
            if (wasDragged()) return;
            e.stopPropagation();
            const r = e.currentTarget.getBoundingClientRect();
            const p = pickAt({
              x: ((e.clientX - r.left) / r.width) * 100,
              y: ((e.clientY - r.top) / r.height) * 100,
            });
            // A tap only seeds the boxes — it is the coarse gesture that gets
            // you close. Adding a building to a wing is an explicit button,
            // because the blocks are ~8 m apart and a tap cannot reliably tell
            // one from its neighbour.
            setPick(p);
          }}
          style={{
            transform: `translate(-50%, -50%) translate(${view.x}px, ${view.y}px) scale(${view.s})`,
            ['--pin-scale' as string]: 1 / view.s,
          }}
        >
          <ResortPlan wings={planWings} stayWings={planStay} />
          {venues.map((venue) => (
            <button
              key={venue.id}
              type="button"
              className={[
                'map-pin',
                venue.id === nextVenueId ? 'is-next' : '',
                venue.id === venueId ? 'is-open' : '',
                showLabels || venue.id === nextVenueId || venue.id === venueId ? 'show-label' : '',
              ].join(' ')}
              style={{ left: `${venue.x}%`, top: `${venue.y}%` }}
              onClick={() => { if (!wasDragged()) navigate(`/map/${venue.id}`); }}
            >
              <span className="dot"><span style={{ transform: 'rotate(45deg)', display: 'grid' }}><IconPinSolid size={14} /></span></span>
              <span className="label">{venue.name}</span>
            </button>
          ))}
          {fix && (
            <>
              {/* The accuracy disc is drawn at the radius the phone reported,
                  in the plan's own metres. A tight fix outdoors is a small
                  disc; a bad one indoors is a huge one, and seeing that is how
                  a guest understands why the dot is vague — far better than a
                  confident dot that happens to be 40 m out. */}
              <span
                className="map-accuracy"
                style={{
                  left: `${fix.pct.x}%`,
                  top: `${fix.pct.y}%`,
                  width: `${fix.accuracyPct * 2}%`,
                  aspectRatio: '1',
                }}
                aria-hidden="true"
              />
              <span
                className="map-me"
                style={{ left: `${fix.pct.x}%`, top: `${fix.pct.y}%` }}
                aria-label={`Roughly where you are, to about ${Math.round(fix.accuracyM)} metres`}
              />
            </>
          )}
          {/* Inside the stage, not the wrap: percentages here are plan
              coordinates, so the marker stays on the spot that was tapped
              through any pan or zoom. */}
          {picking && (
            <span
              className="plan-crosshair"
              style={{ left: `${pick.pct.x}%`, top: `${pick.pct.y}%` }}
              aria-hidden="true"
            />
          )}
        </div>

        <div className="map-zoom">
          <button type="button" className="btn btn-icon btn-secondary" onClick={() => zoomBy(1.6)} aria-label="Zoom in">
            <IconPlus size={16} />
          </button>
          <button type="button" className="btn btn-icon btn-secondary" onClick={() => zoomBy(1 / 1.6)} aria-label="Zoom out">
            <span aria-hidden="true" style={{ fontSize: 18, lineHeight: 1 }}>−</span>
          </button>
          <button type="button" className="btn btn-icon btn-secondary" onClick={reset} aria-label="Reset the view">
            <span aria-hidden="true" style={{ fontSize: 13, fontWeight: 700 }}>⤢</span>
          </button>
        </div>

        {/* The key exists because two things on this plan are coloured, and an
            unexplained colour is just a question. The accent pin in particular
            reads as an inconsistency rather than a signal until it is named. */}
        {(stayWings.length > 0 || nextVenue) && (
          <div className="map-key">
            {nextVenue && (
              <p>
                <span className="map-key-pin" aria-hidden="true" />
                Next up: {nextVenue.name}
              </p>
            )}
            {stayWings.length > 0 && (
              <p>
                <span className="map-key-swatch" aria-hidden="true" />
                Most of us are in wing{stayWings.length > 1 ? 's' : ''} {listOf(stayWings)}
              </p>
            )}
          </div>
        )}

        {liveLocation && (
          <button
            type="button"
            className="btn btn-secondary map-locate"
            onClick={locating === 'watching' || locating === 'asking' ? stop : start}
            disabled={locating === 'unavailable'}
          >
            <IconLocate size={16} />
            {locateLabel(locating, fix?.accuracyM)}
          </button>
        )}
        {picking && (
          <PlanPicker
            pick={pick}
            onPick={setPick}
            group={group}
            onGroup={setGroup}
            label={groupLabel}
            onLabel={setGroupLabel}
            onClose={() => {
              const next = new URLSearchParams(search);
              next.delete('pick');
              setSearch(next, { replace: true });
              setGroup([]);
            }}
          />
        )}
      </div>

      {focused && (
        <div className="sheet">
          <div className="sheet-grab" />
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <span className="venue-thumb" aria-hidden="true">
              <ResortPlan className="venue-thumb-plan" focus={{ x: focused.x, y: focused.y }} label="" />
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h4 style={{ margin: 0 }}>{focused.name}</h4>
              <div style={{ fontSize: 12.5, opacity: 0.7 }}>{focused.where}</div>
              {focused.indoor && (
                /* GPS does not work indoors and the design should not pretend
                   otherwise — say the floor and hand over to the signage. */
                <div style={{ fontSize: 12.5, opacity: 0.7, marginTop: 2 }}>
                  {focused.floor ? `${focused.floor}. ` : ''}Indoors, so your phone will not find you in here. Follow the signs.
                </div>
              )}
            </div>
            <button type="button" className="btn btn-icon btn-secondary" onClick={() => navigate('/map')} aria-label="Close">
              <IconClose size={16} />
            </button>
          </div>

          {(() => {
            const nextHere = model.events.find((e) => e.venueId === focused.id && model.stateOf(e) !== 'past');
            if (!nextHere) return <p style={{ fontSize: 12.5, opacity: 0.6, marginTop: 12 }}>Nothing else is happening here this week.</p>;
            return (
              <Link
                to={`/schedule/${nextHere.id}`}
                className="card"
                style={{ marginTop: 12, background: 'var(--color-surface)', color: 'inherit', textDecoration: 'none', padding: '12px 16px' }}
              >
                <div style={{ fontSize: 11, opacity: 0.6 }}>Next here</div>
                <div style={{ fontFamily: 'var(--font-heading)', fontSize: 18 }}>{nextHere.name}</div>
                <div style={{ fontSize: 12.5, opacity: 0.7 }}>{fmtDay(nextHere.start)} · {fmtTime(nextHere.start)}</div>
              </Link>
            );
          })()}

          <a
            className="btn btn-primary btn-block" style={{ minHeight: 48 }}
            href={focused.navUrl} target="_blank" rel="noreferrer noopener"
          >
            Navigate
          </a>
        </div>
      )}
    </div>
  );
}

/** "2, 3 and 5" — an Oxford-comma-free list, because it reads as a sentence. */
function listOf(items: string[]): string {
  if (items.length < 2) return items.join('');
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/**
 * The button says what is actually happening, including when that is "your
 * phone is not sure". A location control that only ever reads "Locate me" is
 * the one a guest presses four times in a corridor wondering why nothing moved.
 */
function locateLabel(state: LocateState, accuracyM?: number): string {
  switch (state) {
    case 'asking':
      return 'Looking…';
    case 'watching':
      return accuracyM && accuracyM > 40
        ? `Roughly here — to about ${Math.round(accuracyM)} m`
        : 'Following you — tap to stop';
    case 'denied':
      return 'Location is off — the map still works';
    case 'offsite':
      // Not an error. Somebody opening this from home, or from Phuket airport
      // an hour away, should be told they are not there yet rather than shown
      // a dot pinned to the edge of the resort.
      return 'You are not at the resort yet';
    case 'unavailable':
      return 'This phone cannot do location';
    case 'error':
      return 'Could not get a fix — try outside';
    default:
      return 'Locate me';
  }
}
